import { useEffect, useState } from 'react';
import { Bell, BellOff } from 'lucide-react';
import { pushService, type PushState } from '@/services/push';
import { showToast } from '@/components/ui/Toast';

/**
 * Main-menu row that turns the "new work in your queue" push nudge on or off
 * for this device. Hidden entirely when the platform cannot deliver push
 * (e.g. iOS Safari when the app is not installed to the home screen).
 */
export function NotificationsToggle() {
  const [state, setState] = useState<PushState>('unsupported');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void pushService.getState().then(setState);
  }, []);

  if (state === 'unsupported') return null;

  const toggle = async () => {
    setBusy(true);
    try {
      if (state === 'subscribed') {
        await pushService.disable();
        setState('off');
        showToast('New-assignment notifications turned off');
      } else {
        const next = await pushService.enable();
        setState(next);
        if (next === 'subscribed') {
          showToast('You will be notified when new work is assigned');
        } else if (next === 'denied') {
          showToast('Notifications are blocked for this app in your device settings');
        } else {
          showToast('Could not enable notifications. Please try again.');
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const on = state === 'subscribed';
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => void toggle()}
      className="mt-3 flex w-full items-center gap-3 rounded-card border border-brand-border bg-brand-surface px-4 py-3 text-left hover:bg-brand-surfaceAlt disabled:opacity-60"
    >
      {on ? (
        <Bell className="h-5 w-5 shrink-0 text-brand-primaryBlue" />
      ) : (
        <BellOff className="h-5 w-5 shrink-0 text-brand-muted" />
      )}
      <span className="flex-1">
        <span className="block text-sm font-semibold">New assignment notifications</span>
        <span className="block text-xs text-brand-muted">
          {on
            ? 'On - this device is notified when new work is assigned'
            : state === 'denied'
              ? 'Blocked in device settings for this app'
              : 'Off - tap to get notified when new work is assigned'}
        </span>
      </span>
      <span
        className={`rounded px-2 py-0.5 text-[10px] font-bold ${
          on ? 'bg-brand-delivery/15 text-brand-delivery' : 'bg-brand-surfaceAlt text-brand-muted'
        }`}
      >
        {on ? 'ON' : 'OFF'}
      </span>
    </button>
  );
}
